import type { TableProps } from 'ant-design-vue'
import { Tooltip } from 'ant-design-vue'
import type { Dayjs } from 'dayjs'
import { defineComponent, h, ref } from 'vue'
import { formatAmount, formatNumberWithPercent } from '../hooks/useSettlementTable'
export enum PaymentStatus {
  PENDING = 'PENDING',
  UNPAID = 'UNPAID',
  NORMAL = 'NORMAL',
  ABNORMAL = 'ABNORMAL',
}

export enum AbnormalReason {
  NO_PAYMENT_INFO = 'NO_PAYMENT_INFO',
  ACCOUNT_BANNED = 'ACCOUNT_BANNED',
  NO_SUITABLE_ACCOUNT = 'NO_SUITABLE_ACCOUNT',
  DELAYED_PAYMENT = 'DELAYED_PAYMENT',
  INSUFFICIENT_AMOUNT = 'INSUFFICIENT_AMOUNT',
  WRONG_PAYMENT_INFO = 'WRONG_PAYMENT_INFO',
  OTHER = 'OTHER',
}

export interface PaginationProps {
  current: number
  pageSize: number
  total: number
  showSizeChanger?: boolean
  showQuickJumper?: boolean
}

export interface SettlementRecord {
  time: string
  accountNo: string
  payee: string
  bank: string
  bankCardLast4: string
  currency: string
  estimatedIncome: number
  expectedPaymentTime: string
  actualIncome: number
  actualPayment: number
  paymentStatus: PaymentStatus
  abnormalReason?: AbnormalReason
  customAbnormalReason?: string
}

export interface FormState {
  period: [Dayjs | null, Dayjs | null]
  accountId?: number[]
  payChnl?: string
  isExternalSettlement?: boolean
  isNeedExternalSettlement?: boolean
  settlementCurrency?: string
  currency?: string
  receivedStatus?: number
}

export interface SettlementTableProps {
  loading?: boolean
  data?: SettlementRecord[]
  pagination?: TableProps['pagination']
}

export interface UploadResponse {
  fileUrl: string
  originalFilename: string
}

export interface NickNameOption {
  accountName: string
  accountId: number
}

export interface ReqParams {
  fileUrl: string
  accountId?: number
}

export interface TaskItem {
  reqParams: ReqParams
  taskName: string
  taskType: number
}

export interface TaskList {
  list: TaskItem[]
}

export interface UploadFileInfo {
  file: File
  filename: string
}

export const STATUS_OPTIONS = [
  { text: '待确认', value: '0' },
  { text: '未收款', value: '10' },
  { text: '正常收款', value: '20' },
  { text: '异常收款', value: '30' },
]

export const gpColumns = [
  {
    title: '结算月份',
    dataIndex: 'billMonth',
    key: 'billMonth',
    width: 100,
    fixed: 'left',
  },
  {
    title: '账号简称',
    dataIndex: 'accountName',
    key: 'accountName',
    width: 120,
    fixed: 'left',
  },
  {
    title: '收款主体',
    dataIndex: 'collectionSubject',
    key: 'collectionSubject',
    width: 120,
    fixed: 'left',
    sorter: true,
    customRender: ({ text }: { text: string }) => {
      return text || '-'
    },
  },
  {
    title: '收款银行(尾号)',
    key: 'bankInfo',
    width: 140,
  },
  {
    title: '结算币种',
    dataIndex: 'currency',
    key: 'currency',
    width: 100,
  },
  {
    title: '余额',
    dataIndex: 'balance',
    key: 'balance',
    width: 140,
    align: 'right',
    sorter: true,
    customRender: ({ text }: { text: string }) => formatAmount(text),
  },
  {
    title: '预计收款时间',
    dataIndex: 'expectedReceiptTime',
    key: 'expectedReceiptTime',
    width: 140,
    customRender: ({ text }: { text: string }) => {
      return text || '-'
    },
  },
  {
    title: '收入',
    dataIndex: 'actualInvoiceAmount',
    key: 'actualInvoiceAmount',
    width: 120,
    align: 'right',
    sorter: true,
  },
  {
    title: '预扣税',
    dataIndex: 'withholdingTax',
    key: 'withholdingTax',
    customTitle: true,
    tooltip: '含巴西预扣税等',
    width: 160,
    align: 'right',
    sorter: true,
  },
  {
    title: '预估收款金额',
    dataIndex: 'expectReceivedAmount',
    key: 'expectReceivedAmount',
    customTitle: true,
    tooltip: '预估收款金额=余额+收入+预扣税',
    width: 140,
    align: 'right',
    sorter: true,
  },
  {
    title: '实际回款金额',
    dataIndex: 'actualReceivedAmount',
    key: 'actualReceivedAmount',
    width: 120,
    align: 'right',
    sorter: true,
  },
  {
    title: '实际收款时间',
    dataIndex: 'actualReceiptTime',
    key: 'actualReceiptTime',
    width: 140,
    customRender: ({ text }: { text: string }) => {
      return text || '-'
    },
  },
  {
    title: '误差',
    dataIndex: 'receivedDiff',
    key: 'receivedDiff',
    width: 130,
    align: 'right',
    customTitle: true,
    sorter: true,
    fixed: 'right',
    className: 'received-diff-col',
    tooltip: '误差=(实际收款-预计收款)/预计收款',
    customRender: ({ text }: { text: string }) => {
      const formatted = formatNumberWithPercent(text)
      return h(defineComponent({
        setup() {
          const showTooltip = ref(false)
          const spanEl = ref<HTMLElement>()
          return () => h(
            Tooltip,
            { title: showTooltip.value ? formatted : '' },
            () => h('span', {
              ref: spanEl,
              style: { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'block' },
              onMouseenter: () => {
                showTooltip.value = !!spanEl.value && spanEl.value.scrollWidth > spanEl.value.clientWidth
              },
              onMouseleave: () => { showTooltip.value = false },
            }, formatted),
          )
        },
      }))
    },
  },
  {
    title: '收款状态',
    dataIndex: 'receivedStatus',
    key: 'receivedStatus',
    width: 120,
    fixed: 'right',
  },
  {
    title: '回款异常原因',
    width: 160,
    dataIndex: 'receivedResMsg',
    key: 'receivedResMsg',
    fixed: 'right',
    className: 'reason-msg-col',
  },
]
